<script>
import googleCalendarAPI from '../../../../api/googleCalendar';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useAlert } from 'dashboard/composables';

export default {
  data() {
    return {
      isConnected: false,
      isConnecting: false,
      connectionInfo: {},
      calendars: [],
      selectedCalendar: null,
      isSyncing: false,
    };
  },
  mounted() {
    this.loadStatus();
    window.addEventListener('message', this.handleOAuthCallback);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleOAuthCallback);
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      const locale = this.$i18n.locale === 'pt_BR' ? ptBR : enUS;
      return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale });
    },

    async connectGoogleCalendar() {
      try {
        this.isConnecting = true;
        const { data } = await googleCalendarAPI.getAuthorizeUrl();

        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
          data.authorization_url,
          'Google Calendar OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        );
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.CONNECT_FAILED'));
      } finally {
        this.isConnecting = false;
      }
    },

    async handleOAuthCallback(event) {
      if (event.data.type === 'google-calendar-connected') {
        await this.loadStatus();
        await this.loadCalendars();
        useAlert(this.$t('CALENDAR.SYNC_SUCCESS'));
      }
    },

    async disconnect() {
      try {
        await googleCalendarAPI.disconnect();
        this.isConnected = false;
        this.connectionInfo = {};
        this.calendars = [];
        useAlert(this.$t('CALENDAR.DISCONNECT'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.DISCONNECT_FAILED'));
      }
    },

    async loadStatus() {
      try {
        const { data } = await googleCalendarAPI.getStatus();
        this.isConnected = data.connected;
        this.connectionInfo = data;
        this.selectedCalendar = data.default_calendar_id;

        if (this.isConnected) {
          await this.loadCalendars();
        }
      } catch {
        // Silently fail
      }
    },

    async loadCalendars() {
      try {
        const { data } = await googleCalendarAPI.listCalendars();
        this.calendars = data.calendars;
      } catch {
        // Silently fail
      }
    },

    async setDefaultCalendar() {
      try {
        await googleCalendarAPI.setDefaultCalendar(this.selectedCalendar);
        useAlert(this.$t('CALENDAR.EVENT_UPDATED'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.UPDATE_FAILED'));
      }
    },

    async syncNow() {
      this.isSyncing = true;
      try {
        await googleCalendarAPI.sync();
        useAlert(this.$t('CALENDAR.SYNC_STARTED'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.SYNC_FAILED'));
      } finally {
        this.isSyncing = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
          {{ $t('CALENDAR.GOOGLE_CALENDAR') }}
        </h2>
        <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
          {{ $t('CALENDAR.CONNECT_GOOGLE_DESC') }}
        </p>
      </div>
    </div>

    <!-- Not Connected State -->
    <div
      v-if="!isConnected"
      class="border border-slate-200 dark:border-slate-700 rounded-lg p-6 bg-white dark:bg-slate-800"
    >
      <div class="flex flex-col items-center text-center gap-4">
        <div
          class="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        </div>

        <div>
          <h3
            class="font-semibold text-lg mb-2 text-slate-800 dark:text-slate-100"
          >
            {{ $t('CALENDAR.CONNECT_GOOGLE_ACCOUNT') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 max-w-md">
            {{ $t('CALENDAR.CONNECT_GOOGLE_ACCOUNT_DESC') }}
          </p>
        </div>

        <button
          class="flex items-center gap-3 px-6 py-3 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm transition-all hover:shadow font-medium text-slate-700 dark:text-slate-200"
          :disabled="isConnecting"
          @click="connectGoogleCalendar"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span v-if="!isConnecting">{{
            $t('CALENDAR.CONNECT_WITH_GOOGLE')
          }}</span>
          <span v-else>{{ $t('CALENDAR.CONNECTING') }}</span>
        </button>
      </div>
    </div>

    <!-- Connected State -->
    <div
      v-else
      class="border border-slate-200 dark:border-slate-700 rounded-lg p-6 bg-white dark:bg-slate-800"
    >
      <div class="flex items-start justify-between mb-6">
        <div class="flex items-center gap-4">
          <div
            class="w-14 h-14 bg-green-50 dark:bg-green-900/30 rounded-full flex items-center justify-center"
          >
            <svg
              class="w-7 h-7 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <div>
            <div class="flex items-center gap-2 mb-1">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
              >
                {{ $t('CALENDAR.CONNECTED') }}
              </span>
            </div>
            <p class="font-medium text-slate-800 dark:text-slate-100">
              {{ connectionInfo.email }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('CALENDAR.CONNECTED_AT') }}
              {{ formatDate(connectionInfo.connected_at) }}
            </p>
          </div>
        </div>

        <woot-button color-scheme="alert" variant="hollow" @click="disconnect">
          {{ $t('CALENDAR.DISCONNECT') }}
        </woot-button>
      </div>

      <div class="space-y-4">
        <div>
          <label
            class="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300"
          >
            {{ $t('CALENDAR.DEFAULT_CALENDAR') }}
          </label>
          <select
            v-model="selectedCalendar"
            class="form-select w-full dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200"
            @change="setDefaultCalendar"
          >
            <option value="" disabled>
              {{ $t('CALENDAR.SELECT_CALENDAR') }}
            </option>
            <option v-for="cal in calendars" :key="cal.id" :value="cal.id">
              {{ cal.name }} {{ cal.primary ? $t('CALENDAR.PRIMARY') : '' }}
            </option>
          </select>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {{ $t('CALENDAR.DEFAULT_CALENDAR_DESC') }}
          </p>
        </div>

        <!-- Sync Info -->
        <div
          class="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4"
        >
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h4 class="font-medium text-sm text-blue-800 dark:text-blue-300">
                {{ $t('CALENDAR.BIDIRECTIONAL_SYNC') }}
              </h4>
              <p class="text-xs text-blue-700 dark:text-blue-400 mt-1">
                {{ $t('CALENDAR.BIDIRECTIONAL_SYNC_DESC') }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700"
        >
          <div>
            <h4 class="font-medium text-sm text-slate-800 dark:text-slate-200">
              {{ $t('CALENDAR.SYNC_MANUALLY') }}
            </h4>
            <p class="text-xs text-slate-600 dark:text-slate-400">
              {{ $t('CALENDAR.SYNC_MANUALLY_DESC') }}
            </p>
          </div>

          <woot-button
            color-scheme="primary"
            variant="hollow"
            :is-loading="isSyncing"
            @click="syncNow"
          >
            <div class="flex items-center gap-2">
              <fluent-icon
                icon="arrow-sync"
                size="16"
                :class="{ 'animate-spin': isSyncing }"
              />
              {{ $t('CALENDAR.SYNC_NOW') }}
            </div>
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>
